// US Extended Protection Plan provider data
// All defaults are based on public US warranty plan specifications (as of 2026):
//   ï¿½ Magnuson-Moss Warranty Act coverage standards
//   ï¿½ Individual retailer/manufacturer published terms
//   ï¿½ FTC Service Contract Disclosure requirements

export type ExtProviderType = 'retailer' | 'manufacturer' | 'thirdParty'
export type ExtThirdPartySubType = 'perItem' | 'subscription' | 'homeWarranty'
export type ExtServiceType = 'inHome' | 'depot' | 'carryIn' | 'onSite'

export const SERVICE_TYPE_LABELS: Record<ExtServiceType, string> = {
  inHome: 'In-Home Service',
  depot: 'Depot / Mail-In Repair',
  carryIn: 'Carry-In Service',
  onSite: 'On-Site Service',
}

// -- Retailer Providers --------------------------------------------------------

export interface RetailerProvider {
  id: string
  name: string
  planBrandName: string
  /** Available plan durations in years */
  durationOptions: number[]
  /** Per-claim deductible in USD ($0 for nearly all US retailer plans as of 2026) */
  deductibleAmount: number
  defaultServiceType: ExtServiceType
  claimsPhone: string
  claimsWebsite: string
}

export const kRetailerProviders: RetailerProvider[] = [
  {
    id: 'bestbuy', name: 'Best Buy', planBrandName: 'Geek Squad Protection',
    durationOptions: [2, 3, 4, 5], deductibleAmount: 0, defaultServiceType: 'inHome',
    claimsPhone: '1-800-433-5778', claimsWebsite: 'bestbuy.com/geek-squad',
  },
  {
    id: 'walmart', name: 'Walmart', planBrandName: 'Walmart Protection Plan',
    durationOptions: [2, 3], deductibleAmount: 0, defaultServiceType: 'inHome',
    claimsPhone: '1-877-968-6391', claimsWebsite: 'squaretrade.com/walmart',
  },
  {
    id: 'homedepot', name: 'Home Depot', planBrandName: 'The Home Depot Protection Plan',
    durationOptions: [2, 3, 5], deductibleAmount: 0, defaultServiceType: 'inHome',
    claimsPhone: '1-800-466-3337', claimsWebsite: 'homedepot.com/c/protection_plans',
  },
  {
    id: 'lowes', name: "Lowe's", planBrandName: "Lowe's Protection Plan",
    durationOptions: [2, 3, 5], deductibleAmount: 0, defaultServiceType: 'inHome',
    claimsPhone: '1-888-775-6937', claimsWebsite: 'lowes.com/l/protection-plans.html',
  },
  {
    id: 'costco', name: 'Costco', planBrandName: 'Costco Protection Plan',
    durationOptions: [2, 3, 4], deductibleAmount: 0, defaultServiceType: 'depot',
    claimsPhone: '1-800-774-2678', claimsWebsite: 'allstateprotectionplans.com/costco',
  },
  {
    id: 'target', name: 'Target', planBrandName: 'Target Protection Plan',
    durationOptions: [2, 3], deductibleAmount: 0, defaultServiceType: 'depot',
    claimsPhone: '1-877-634-5186', claimsWebsite: 'squaretrade.com/target',
  },
  {
    id: 'samsclub', name: "Sam's Club", planBrandName: "Sam's Club Protection Plan",
    durationOptions: [2, 3], deductibleAmount: 0, defaultServiceType: 'inHome',
    claimsPhone: '1-800-726-8787', claimsWebsite: 'samsclub.com/p/protection-plans',
  },
  {
    id: 'brandsmart', name: 'BrandSmart', planBrandName: 'BrandSmart Protection Plan',
    durationOptions: [1, 2, 3], deductibleAmount: 0, defaultServiceType: 'inHome',
    claimsPhone: '1-954-797-4000', claimsWebsite: 'brandsmart.com',
  },
  {
    id: 'amazon', name: 'Amazon', planBrandName: 'Amazon Protection Plan',
    durationOptions: [1, 2, 3], deductibleAmount: 0, defaultServiceType: 'depot',
    claimsPhone: '1-888-280-4331', claimsWebsite: 'amazon.com/gp/help/customer/display.html',
  },
]

// -- Manufacturer Providers ----------------------------------------------------

export interface ManufacturerPlanTier {
  id: string
  name: string
  durationOptions: number[]
  /** Per-incident service fee in USD. $0 if no deductible. Use deductibleNote for tiered fees. */
  deductibleAmount: number
  /** Present for plans with tiered service fees (Samsung Care+, AppleCare+) */
  deductibleNote?: string
  defaultServiceType: ExtServiceType
  /** true = plan runs concurrent with mfg warranty (starts on purchase date) */
  startsOnPurchaseDate: boolean
  /** true = also available as a monthly rolling subscription (no fixed end date) */
  supportsMonthly: boolean
}

export interface ManufacturerProvider {
  id: string
  name: string
  isMultiTier: boolean
  /** Used when isMultiTier = false */
  defaultTier?: ManufacturerPlanTier
  /** Used when isMultiTier = true */
  tiers: ManufacturerPlanTier[]
  claimsPhone: string
  claimsWebsite: string
  /** Asset brand strings that auto-highlight this manufacturer in the selection list */
  brandAliases: string[]
}

export const kManufacturerProviders: ManufacturerProvider[] = [

  // â”€â”€â”€ Consumer Electronics / Mobile â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  {
    // Samsung has two distinct plans: Care+ (phones/wearables, concurrent) vs Protection Plus (appliances/TVs, sequential)
    id: 'samsung', name: 'Samsung', isMultiTier: true,
    claimsPhone: '1-800-726-7864', claimsWebsite: 'samsung.com/us/support',
    brandAliases: ['samsung', 'samsung electronics'],
    tiers: [
      {
        id: 'samsung_care_plus', name: 'Samsung Care+',
        durationOptions: [2, 3], deductibleAmount: 29,
        deductibleNote: '/screen repair, /other accidental damage, max 2 claims/yr,  hardware failure',
        defaultServiceType: 'carryIn', startsOnPurchaseDate: true, supportsMonthly: true,
      },
      {
        id: 'samsung_care_plus_theft', name: 'Samsung Care+ with Theft & Loss',
        durationOptions: [2, 3], deductibleAmount: 99,
        deductibleNote: 'Same as Care+ plus theft/loss replacement (max 2 claims/12 months)',
        defaultServiceType: 'carryIn', startsOnPurchaseDate: true, supportsMonthly: true,
      },
      {
        id: 'samsung_protection_plus', name: 'Samsung Protection Plus',
        durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // AppleCare+ for all Apple devices; Theft & Loss variant available for iPhone/iPad
    id: 'apple', name: 'Apple', isMultiTier: true,
    claimsPhone: '1-800-275-2273', claimsWebsite: 'support.apple.com',
    brandAliases: ['apple', 'apple inc'],
    tiers: [
      {
        id: 'applecare_plus', name: 'AppleCare+',
        durationOptions: [2, 3], deductibleAmount: 29,
        deductibleNote: '/screen or glass, /other accidental, max 2 incidents/12mo,  hardware failure,  battery service (< 80% capacity)',
        defaultServiceType: 'carryIn', startsOnPurchaseDate: true, supportsMonthly: true,
      },
      {
        id: 'applecare_plus_theft', name: 'AppleCare+ with Theft and Loss',
        durationOptions: [2, 3], deductibleAmount: 149,
        deductibleNote: 'Same as AppleCare+ plus theft/loss replacement. - per incident depending on device.',
        defaultServiceType: 'carryIn', startsOnPurchaseDate: true, supportsMonthly: true,
      },
    ],
  },

  {
    // Preferred Care for Pixel phones, tablets, Nest devices
    id: 'google', name: 'Google', isMultiTier: true,
    claimsPhone: '1-844-613-5566', claimsWebsite: 'store.google.com/us/category/preferred-care',
    brandAliases: ['google', 'google pixel', 'pixel'],
    tiers: [
      {
        id: 'google_preferred_care', name: 'Google Preferred Care',
        durationOptions: [2], deductibleAmount: 29,
        deductibleNote: '/screen repair, /other accidental damage, max 2 incidents/12mo',
        defaultServiceType: 'carryIn', startsOnPurchaseDate: true, supportsMonthly: false,
      },
      {
        id: 'google_preferred_care_monthly', name: 'Google Preferred Care (Monthly)',
        durationOptions: [2], deductibleAmount: 29,
        deductibleNote: 'Same coverage as Preferred Care, billed monthly with no fixed end date.',
        defaultServiceType: 'carryIn', startsOnPurchaseDate: true, supportsMonthly: true,
      },
    ],
  },

  {
    // Microsoft Complete: Surface devices. Complete for Business adds on-site + priority support.
    id: 'microsoft', name: 'Microsoft', isMultiTier: true,
    claimsPhone: '1-800-642-7676', claimsWebsite: 'microsoft.com/en-us/store/b/microsoft-complete',
    brandAliases: ['microsoft', 'surface', 'microsoft surface'],
    tiers: [
      {
        id: 'ms_complete', name: 'Microsoft Complete',
        durationOptions: [2, 4], deductibleAmount: 49,
        deductibleNote: '- per incident (varies by device). Accidental damage up to 2 claims/yr.',
        defaultServiceType: 'depot', startsOnPurchaseDate: true, supportsMonthly: false,
      },
      {
        id: 'ms_complete_business', name: 'Microsoft Complete for Business',
        durationOptions: [2, 4], deductibleAmount: 0,
        defaultServiceType: 'onSite', startsOnPurchaseDate: true, supportsMonthly: false,
      },
    ],
  },

  {
    // Sony Protection Plan (standard) | Protect Plus (adds accidental damage)
    id: 'sony', name: 'Sony', isMultiTier: true,
    claimsPhone: '1-800-222-7669', claimsWebsite: 'sony.com/en/articles/contact-support',
    brandAliases: ['sony', 'sony electronics'],
    tiers: [
      {
        id: 'sony_protection_plan', name: 'Sony Protection Plan',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'sony_protect_plus', name: 'Sony Protect Plus',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        deductibleNote: 'Adds coverage for accidental damage (drops, spills, electrical surges).',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  // â”€â”€â”€ Computers / Laptops â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  {
    // Premium Support: 24/7 tech + hardware next-day on-site. Premium Support Plus adds ADP.
    id: 'dell', name: 'Dell', isMultiTier: true,
    claimsPhone: '1-800-624-9897', claimsWebsite: 'dell.com/support',
    brandAliases: ['dell', 'dell technologies', 'alienware'],
    tiers: [
      {
        id: 'dell_premium_support', name: 'Dell Premium Support',
        durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0,
        defaultServiceType: 'onSite', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'dell_premium_support_plus', name: 'Dell Premium Support Plus',
        durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0,
        deductibleNote: 'Includes accidental damage protection + predictive issue detection.',
        defaultServiceType: 'onSite', startsOnPurchaseDate: true, supportsMonthly: false,
      },
    ],
  },

  {
    // HP Care Pack: next-business-day on-site. Care Pack with ADP adds accidental damage.
    id: 'hp', name: 'HP', isMultiTier: true,
    claimsPhone: '1-800-474-6836', claimsWebsite: 'support.hp.com',
    brandAliases: ['hp', 'hewlett packard', 'hewlett-packard'],
    tiers: [
      {
        id: 'hp_carepack', name: 'HP Care Pack',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'onSite', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'hp_carepack_adp', name: 'HP Care Pack with Accidental Damage Protection',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        deductibleNote: 'Adds coverage for drops, spills, and electrical damage.',
        defaultServiceType: 'onSite', startsOnPurchaseDate: true, supportsMonthly: false,
      },
    ],
  },

  {
    // Lenovo Premium Care: 24/7 + next-day on-site. Premium Care Plus adds accidental damage.
    id: 'lenovo', name: 'Lenovo', isMultiTier: true,
    claimsPhone: '1-800-426-7378', claimsWebsite: 'support.lenovo.com',
    brandAliases: ['lenovo', 'thinkpad', 'thinkcentre', 'ideapad', 'legion'],
    tiers: [
      {
        id: 'lenovo_premium_care', name: 'Lenovo Premium Care',
        durationOptions: [2, 3, 4, 5], deductibleAmount: 0,
        defaultServiceType: 'onSite', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'lenovo_premium_care_plus', name: 'Lenovo Premium Care Plus',
        durationOptions: [2, 3, 4, 5], deductibleAmount: 0,
        deductibleNote: 'Includes accidental damage protection (drops, spills, surges).',
        defaultServiceType: 'onSite', startsOnPurchaseDate: true, supportsMonthly: false,
      },
    ],
  },

  {
    // ASUS Premium Care: standard on-site repair. ADP adds accidental damage.
    id: 'asus', name: 'ASUS', isMultiTier: true,
    claimsPhone: '1-888-678-3688', claimsWebsite: 'asus.com/us/support',
    brandAliases: ['asus', 'asus laptop', 'rog', 'asus rog'],
    tiers: [
      {
        id: 'asus_premium_care', name: 'ASUS Premium Care',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'onSite', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'asus_adp', name: 'ASUS Accidental Damage Protection (ADP)',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        deductibleNote: 'Covers accidental drops, liquid spills, and electrical surges.',
        defaultServiceType: 'depot', startsOnPurchaseDate: true, supportsMonthly: false,
      },
    ],
  },

  {
    // Acer Care Plus: standard extended warranty. On-Site service plan is premium tier.
    id: 'acer', name: 'Acer', isMultiTier: true,
    claimsPhone: '1-866-695-2237', claimsWebsite: 'acer.com/us-en/support',
    brandAliases: ['acer', 'acer laptop', 'predator', 'acer predator'],
    tiers: [
      {
        id: 'acer_care_plus', name: 'Acer Care Plus',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'depot', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'acer_extended_service', name: 'Acer Extended Service Plan (On-Site)',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'onSite', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  // â”€â”€â”€ Home Appliances â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  {
    // LG Premium Care (phones/electronics, concurrent + accidental) | LG Extended (appliances)
    id: 'lg', name: 'LG', isMultiTier: true,
    claimsPhone: '1-800-243-0000', claimsWebsite: 'lg.com/us/support',
    brandAliases: ['lg', 'lg electronics'],
    tiers: [
      {
        id: 'lg_premium_care', name: 'LG Premium Care',
        durationOptions: [2, 3], deductibleAmount: 49,
        deductibleNote: ' per accidental damage claim (phones/tablets). Max 2 claims/yr.',
        defaultServiceType: 'carryIn', startsOnPurchaseDate: true, supportsMonthly: false,
      },
      {
        id: 'lg_extended_warranty', name: 'LG Extended Warranty Plan',
        durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // Standard Extended Service Plan | Premium adds food loss + priority scheduling
    id: 'whirlpool', name: 'Whirlpool', isMultiTier: true,
    claimsPhone: '1-800-253-1301', claimsWebsite: 'whirlpool.com/support',
    brandAliases: ['whirlpool', 'whirlpool corporation'],
    tiers: [
      {
        id: 'whirlpool_esp', name: 'Whirlpool Extended Service Plan',
        durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'whirlpool_premium', name: 'Whirlpool Premium Service Plan',
        durationOptions: [3, 5], deductibleAmount: 0,
        deductibleNote: 'Includes food/medicine loss reimbursement (up to ) + priority scheduling.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // GE Protection Plus (standard 1-3yr) | Protection Advantage (premium 3-5yr, no-lemon)
    id: 'ge', name: 'GE Appliances', isMultiTier: true,
    claimsPhone: '1-800-432-2737', claimsWebsite: 'geappliances.com/ge/service-and-support',
    brandAliases: ['ge', 'ge appliances', 'general electric', 'ge profile', 'ge cafe', 'monogram'],
    tiers: [
      {
        id: 'ge_protection_plus', name: 'GE Service Protection Plus',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'ge_protection_advantage', name: 'GE Service Protection Advantage',
        durationOptions: [3, 4, 5], deductibleAmount: 0,
        deductibleNote: 'Adds no-lemon guarantee, food spoilage (), power surge (), same-day service.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // KitchenAid (Whirlpool brand): Standard ESP | Premium with food loss
    id: 'kitchenaid', name: 'KitchenAid', isMultiTier: true,
    claimsPhone: '1-800-422-1230', claimsWebsite: 'kitchenaid.com/support',
    brandAliases: ['kitchenaid'],
    tiers: [
      {
        id: 'ka_extended_service', name: 'KitchenAid Extended Service Plan',
        durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'ka_premium_service', name: 'KitchenAid Premium Service Plan',
        durationOptions: [3, 5], deductibleAmount: 0,
        deductibleNote: 'Includes food/medicine loss reimbursement + no-lemon guarantee.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // Standard Service Plan | Premium adds food loss + priority scheduling
    id: 'maytag', name: 'Maytag', isMultiTier: true,
    claimsPhone: '1-800-344-1274', claimsWebsite: 'maytag.com/customer-service',
    brandAliases: ['maytag'],
    tiers: [
      {
        id: 'maytag_service_plan', name: 'Maytag Service Plan',
        durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'maytag_premium_service', name: 'Maytag Premium Service Plan',
        durationOptions: [3, 5], deductibleAmount: 0,
        deductibleNote: 'Adds food/medicine loss (), no-lemon guarantee, priority scheduling.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // Standard Appliance Service Plan | Benchmark Protection Plan (premium, longer terms)
    id: 'bosch', name: 'Bosch', isMultiTier: true,
    claimsPhone: '1-800-944-2904', claimsWebsite: 'bosch-home.com/us',
    brandAliases: ['bosch', 'bosch appliances'],
    tiers: [
      {
        id: 'bosch_appliance_service', name: 'Bosch Appliance Service Plan',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'bosch_benchmark', name: 'Bosch Benchmark Protection Plan',
        durationOptions: [3, 5], deductibleAmount: 0,
        deductibleNote: 'Premium protection for Bosch Benchmark series appliances with priority repair.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // Standard Service Plan | Preferred Service Plan (extends to 5yr, food loss)
    id: 'frigidaire', name: 'Frigidaire', isMultiTier: true,
    claimsPhone: '1-800-374-4432', claimsWebsite: 'frigidaire.com/support',
    brandAliases: ['frigidaire', 'frigidaire gallery', 'frigidaire professional'],
    tiers: [
      {
        id: 'frigidaire_service_plan', name: 'Frigidaire Service Plan',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'frigidaire_preferred', name: 'Frigidaire Preferred Service Plan',
        durationOptions: [3, 5], deductibleAmount: 0,
        deductibleNote: 'Adds food spoilage reimbursement and no-lemon guarantee.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    id: 'electrolux', name: 'Electrolux', isMultiTier: true,
    claimsPhone: '1-877-435-3287', claimsWebsite: 'electroluxappliances.com/service',
    brandAliases: ['electrolux', 'electrolux appliances'],
    tiers: [
      {
        id: 'elux_service_plan', name: 'Electrolux Service Plan',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'elux_premium', name: 'Electrolux Premium Protection Plan',
        durationOptions: [3, 5], deductibleAmount: 0,
        deductibleNote: 'Includes food spoilage reimbursement and priority in-home service.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // MasterCare (standard) | MasterCare Plus (annual inspection + priority + pre-stocked parts)
    id: 'miele', name: 'Miele', isMultiTier: true,
    claimsPhone: '1-800-843-7231', claimsWebsite: 'mieleusa.com/service',
    brandAliases: ['miele'],
    tiers: [
      {
        id: 'miele_mastercare', name: 'Miele MasterCare Extended Warranty',
        durationOptions: [3, 5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'miele_mastercare_plus', name: 'Miele MasterCare Plus',
        durationOptions: [5], deductibleAmount: 0,
        deductibleNote: 'Premium: annual inspection + priority scheduling + pre-stocked parts.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  // â”€â”€â”€ HVAC / Climate â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  {
    // 5-Year Parts (default) | 10-Year Registered Parts (requires registration within 60 days)
    id: 'rheem', name: 'Rheem', isMultiTier: true,
    claimsPhone: '1-800-432-8373', claimsWebsite: 'rheem.com/warranty',
    brandAliases: ['rheem', 'rheem sales company', 'rheem manufacturing', 'ruud'],
    tiers: [
      {
        id: 'rheem_5yr', name: 'Rheem 5-Year Parts Limited Warranty',
        durationOptions: [5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'rheem_10yr', name: 'Rheem 10-Year Registered Parts Warranty',
        durationOptions: [10], deductibleAmount: 0,
        deductibleNote: 'Requires product registration within 60 days of installation.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // 5-Year Parts | 10-Year Registered (requires registration within 90 days)
    id: 'carrier', name: 'Carrier', isMultiTier: true,
    claimsPhone: '1-800-227-7437', claimsWebsite: 'carrier.com/residential/en/us/support',
    brandAliases: ['carrier', 'carrier hvac', 'carrier global'],
    tiers: [
      {
        id: 'carrier_5yr', name: 'Carrier 5-Year Parts Limited Warranty',
        durationOptions: [5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'carrier_10yr', name: 'Carrier 10-Year Registered Limited Warranty',
        durationOptions: [10], deductibleAmount: 0,
        deductibleNote: 'Requires registration within 90 days of installation.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // 5-Year Parts | 10-Year Registered Parts (requires registration)
    id: 'lennox', name: 'Lennox', isMultiTier: true,
    claimsPhone: '1-800-953-6669', claimsWebsite: 'lennox.com/support',
    brandAliases: ['lennox', 'lennox industries', 'lennox hvac'],
    tiers: [
      {
        id: 'lennox_5yr', name: 'Lennox 5-Year Parts Limited Warranty',
        durationOptions: [5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'lennox_10yr', name: 'Lennox 10-Year Registered Parts Warranty',
        durationOptions: [10], deductibleAmount: 0,
        deductibleNote: 'Requires product registration to activate extended coverage.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // Base 5-Year Parts | Registered 10-Year Parts | Comfort Protection Plan (labor extension)
    id: 'trane', name: 'Trane', isMultiTier: true,
    claimsPhone: '1-800-945-5884', claimsWebsite: 'trane.com/residential/en/resources/warranty',
    brandAliases: ['trane', 'american standard', 'american standard heating', 'american standard hvac'],
    tiers: [
      {
        id: 'trane_base', name: 'Trane Base Limited Warranty (5-Year Parts)',
        durationOptions: [5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'trane_registered', name: 'Trane Registered Limited Warranty (10-Year Parts)',
        durationOptions: [10], deductibleAmount: 0,
        deductibleNote: 'Requires registration within 60 days of installation.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'trane_labor', name: 'Trane Comfort Protection Plan (Labor Extension)',
        durationOptions: [1, 2, 3, 5], deductibleAmount: 0,
        deductibleNote: 'Extends labor coverage beyond standard warranty. Sold through authorized Trane dealers.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // Goodman / Amana / Daikin: 10-Year Registered Parts | Labor Extension
    id: 'goodman', name: 'Goodman', isMultiTier: true,
    claimsPhone: '1-877-254-4729', claimsWebsite: 'goodmanmfg.com/support',
    brandAliases: ['goodman', 'goodman manufacturing', 'amana', 'amana hvac', 'daikin', 'daikin hvac'],
    tiers: [
      {
        id: 'goodman_10yr', name: 'Goodman 10-Year Registered Parts Warranty',
        durationOptions: [10], deductibleAmount: 0,
        deductibleNote: 'Requires registration within 60 days of installation.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'goodman_labor', name: 'Goodman Labor Warranty Extension',
        durationOptions: [1, 2, 3], deductibleAmount: 0,
        deductibleNote: 'Extends labor cost coverage. Sold through authorized Goodman dealers.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },

  {
    // York / Coleman: 5-Year Parts | 10-Year Registered (requires registration within 90 days)
    id: 'york', name: 'York', isMultiTier: true,
    claimsPhone: '1-800-861-9675', claimsWebsite: 'york.com/support',
    brandAliases: ['york', 'york hvac', 'york international', 'coleman hvac'],
    tiers: [
      {
        id: 'york_5yr', name: 'York 5-Year Parts Limited Warranty',
        durationOptions: [5], deductibleAmount: 0,
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
      {
        id: 'york_10yr', name: 'York 10-Year Registered Limited Warranty',
        durationOptions: [10], deductibleAmount: 0,
        deductibleNote: 'Requires registration within 90 days of installation.',
        defaultServiceType: 'inHome', startsOnPurchaseDate: false, supportsMonthly: false,
      },
    ],
  },
]
/** Find a manufacturer provider by free-text name (used when user types a name manually). */
export function findManufacturerByName(text: string): ManufacturerProvider | null {
  const raw = text.toLowerCase().trim()
  if (!raw) return null
  return kManufacturerProviders.find(m =>
    m.brandAliases.some(a => {
      const alias = a.toLowerCase()
      return raw === alias || raw.startsWith(alias) || alias.startsWith(raw)
    })
  ) ?? null
}

export interface ThirdPartyProvider {
  id: string
  name: string
  subType: ExtThirdPartySubType
  /** Present for fixed-term per-item plans */
  durationOptions?: number[]
  deductibleAmount: number
  defaultServiceType: ExtServiceType
  /** Present for subscription plans */
  monthlyFee?: number
  isSubscription: boolean
  claimsPhone: string
  claimsWebsite: string
}

/** Per-item (individual product) extended warranty providers */
export const kPerItemProviders: ThirdPartyProvider[] = [
  {
    id: 'allstate', name: 'Allstate Protection Plans', subType: 'perItem',
    durationOptions: [2, 3, 4], deductibleAmount: 0, defaultServiceType: 'inHome',
    isSubscription: false, claimsPhone: '1-877-927-7268', claimsWebsite: 'allstateprotectionplans.com',
  },
  {
    id: 'assurant', name: 'Assurant Product Protection', subType: 'perItem',
    durationOptions: [1, 2, 3, 4, 5], deductibleAmount: 0, defaultServiceType: 'inHome',
    isSubscription: false, claimsPhone: '1-888-260-7736', claimsWebsite: 'assurant.com/warranty',
  },
  {
    id: 'upsie', name: 'Upsie Extended Warranty', subType: 'perItem',
    durationOptions: [2, 3, 5], deductibleAmount: 25, defaultServiceType: 'depot',
    isSubscription: false, claimsPhone: '1-888-444-8776', claimsWebsite: 'upsie.com/claims',
  },
  {
    id: 'extend', name: 'Extend Product Protection', subType: 'perItem',
    durationOptions: [1, 2, 3], deductibleAmount: 0, defaultServiceType: 'depot',
    isSubscription: false, claimsPhone: '1-877-248-7707', claimsWebsite: 'extend.com',
  },
]

/** Subscription (whole-home device coverage) providers */
export const kSubscriptionProviders: ThirdPartyProvider[] = [
  {
    id: 'asurion', name: 'Asurion Home+', subType: 'subscription',
    deductibleAmount: 0, defaultServiceType: 'inHome', monthlyFee: 24.99, isSubscription: true,
    claimsPhone: '1-844-529-2576', claimsWebsite: 'asurion.com/home-plus',
  },
]

export interface HomeWarrantyProvider {
  id: string
  name: string
  /** Standard service fee per trade visit in USD */
  serviceFee: number
  claimsPhone: string
  claimsWebsite: string
}

/** Home warranty (whole-home systems & appliances) providers */
export const kHomeWarrantyProviders: HomeWarrantyProvider[] = [
  { id: 'ahs',           name: 'American Home Shield',         serviceFee: 100, claimsPhone: '1-888-682-1043', claimsWebsite: 'ahs.com'                  },
  { id: 'choice',        name: 'Choice Home Warranty',         serviceFee: 85,  claimsPhone: '1-888-531-5403', claimsWebsite: 'choicehomewarranty.com'   },
  { id: 'firstamerican', name: 'First American Home Warranty',  serviceFee: 85,  claimsPhone: '1-800-992-3400', claimsWebsite: 'firstam.com/home-warranty' },
  { id: '2-10',          name: '2-10 Home Buyers Warranty',    serviceFee: 100, claimsPhone: '1-800-795-9595', claimsWebsite: '2-10.com'                  },
  { id: 'selecthw',      name: 'Select Home Warranty',         serviceFee: 75,  claimsPhone: '1-855-267-3532', claimsWebsite: 'selecthomewarranty.com'   },
]
